<template>
  <div class="dashboard-shell pos-dashboard">
    <div class="dashboard-head">
      <div>
        <h2>Business Dashboard</h2>
        <p>Live sales, profit, stock alerts, cash position, receivables and cheques.</p>
      </div>
      <button class="refresh-btn" @click="refreshDashboard" :disabled="loading">
        {{ loading ? "Refreshing..." : "Refresh Dashboard" }}
      </button>
    </div>

    <div class="loading-banner" v-if="loading && !hasData">Loading dashboard data...</div>

    <div class="kpi-grid">
      <div
        v-for="card in kpiCards"
        :key="card.title"
        class="metric-card"
        :class="card.tone"
      >
        <div class="metric-icon"><vue-feather :type="card.icon" class="metric-feather"></vue-feather></div>
        <div class="metric-content">
          <span class="metric-label">{{ card.title }}</span>
          <strong class="metric-value">{{ card.value }}</strong>
          <small class="metric-note" :class="card.noteTone">{{ card.note }}</small>
        </div>
      </div>
    </div>

    <div class="cash-grid">
      <div class="dashboard-card cash-panel">
        <div class="card-head panel-head">
          <div class="title-wrap">
            <span class="head-icon"><vue-feather type="credit-card"></vue-feather></span>
            <div>
              <h4>Cash, receivables and cheques</h4>
              <p>Working capital that needs attention today.</p>
            </div>
          </div>
          <button class="link-btn" @click="$router.push('/bank-cheques')">Open Cheques</button>
        </div>
        <div class="cash-metrics">
          <div v-for="item in cashCards" :key="item.title" class="cash-metric">
            <span>{{ item.title }}</span>
            <strong>{{ item.value }}</strong>
            <small :class="item.tone">{{ item.note }}</small>
          </div>
        </div>
      </div>

      <div class="dashboard-card cheque-panel">
        <div class="card-head panel-head">
          <div class="title-wrap">
            <span class="head-icon warn"><vue-feather type="file-text"></vue-feather></span>
            <div>
              <h4>Cheque Register</h4>
              <p>Pending clearance, incoming and issued cheques.</p>
            </div>
          </div>
        </div>
        <div class="cheque-list" v-if="chequeRows.length">
          <div v-for="cheque in chequeRows" :key="cheque.chequeid" class="cheque-row">
            <div>
              <strong>{{ cheque.party_name }}</strong>
              <span>#{{ cheque.cheque_number }} &middot; {{ formatDate(cheque.expected_clearance_date) }}</span>
            </div>
            <div class="text-end">
              <strong>{{ formatCurrency(cheque.amount) }}</strong>
              <span class="status-pill" :class="chequeStatusClass(cheque.status)">{{ prettyStatus(cheque.status) }}</span>
            </div>
          </div>
        </div>
        <div class="empty-state compact" v-else>No cheque entries to show.</div>
      </div>
    </div>

    <section ref="trendSection" class="lazy-section" data-section="trend">
      <div v-if="isSectionVisible('trend')" class="chart-grid">
        <div class="dashboard-card chart-card">
          <div class="card-head panel-head">
            <div class="title-wrap">
              <span class="head-icon"><vue-feather type="shopping-cart"></vue-feather></span>
              <div>
                <h4>Sales & Purchase</h4>
                <p>Revenue collected vs amount spent on stock.</p>
              </div>
            </div>
            <div class="range-tabs">
              <button>1D</button>
              <button>1W</button>
              <button>1M</button>
              <button>3M</button>
              <button>6M</button>
              <button class="active">1Y</button>
            </div>
          </div>
          <div class="chart-summary">
            <span><i></i>Total Purchase <strong>{{ compactCurrency(monthPurchaseTotal) }}</strong></span>
            <span><i class="sales-dot"></i>Total Sales <strong>{{ compactCurrency(monthSalesTotal) }}</strong></span>
          </div>
          <apexchart
            type="bar"
            height="320"
            :options="salesPurchaseChartOptions"
            :series="salesPurchaseSeries"
          />
        </div>

        <div class="dashboard-card chart-card">
          <div class="card-head panel-head">
            <div class="title-wrap">
              <span class="head-icon danger"><vue-feather type="alert-triangle"></vue-feather></span>
              <div>
                <h4>Sales Statics</h4>
                <p>Revenue and expense movement across the year.</p>
              </div>
            </div>
            <button class="year-btn"><vue-feather type="calendar" class="btn-feather"></vue-feather> 2026</button>
          </div>
          <div class="stat-summary">
            <div>
              <strong class="success-text">{{ formatCurrency(thisMonthRevenue) }}</strong>
              <span>Revenue</span>
            </div>
            <div>
              <strong class="danger-text">{{ formatCurrency(thisMonthExpense) }}</strong>
              <span>Expense</span>
            </div>
          </div>
          <apexchart
            type="bar"
            height="320"
            :options="profitTrendChartOptions"
            :series="profitTrendSeries"
          />
        </div>
      </div>
      <div v-else class="section-placeholder">
        <strong>Trend charts will load when this section enters the viewport.</strong>
      </div>
    </section>

    <section ref="analyticsSection" class="lazy-section" data-section="analytics">
      <div v-if="isSectionVisible('analytics')" class="analytics-grid">
        <div class="dashboard-card chart-card">
          <div class="card-head">
            <div>
              <h4>Top 5 items by revenue (this month)</h4>
              <p>Sales amount by item, not just units.</p>
            </div>
          </div>
          <apexchart
            v-if="topItemsRevenue.length"
            type="bar"
            height="330"
            :options="topRevenueChartOptions"
            :series="topRevenueChartSeries"
          />
          <div class="empty-state" v-else>No revenue data yet.</div>
        </div>

        <div class="dashboard-card chart-card">
          <div class="card-head">
            <div>
              <h4>Top 5 items by profit</h4>
              <p>Revenue minus FIFO cost price per item.</p>
            </div>
          </div>
          <apexchart
            v-if="topItemsProfit.length"
            type="bar"
            height="330"
            :options="topProfitChartOptions"
            :series="topProfitChartSeries"
          />
          <div class="empty-state" v-else>No profit data yet.</div>
        </div>

        <div class="dashboard-card chart-card">
          <div class="card-head">
            <div>
              <h4>Category Revenue</h4>
              <p>Revenue split by item category for the current month.</p>
            </div>
          </div>
          <apexchart
            v-if="categoryRevenue.length"
            type="donut"
            height="330"
            :options="categoryRevenueOptions"
            :series="categoryRevenueSeries"
          />
          <div class="empty-state" v-else>No category revenue data yet.</div>
        </div>
      </div>
      <div v-else class="section-placeholder">
        <strong>Item analytics will load when you scroll here.</strong>
      </div>
    </section>

    <section ref="operationsSection" class="lazy-section" data-section="operations">
      <div v-if="isSectionVisible('operations')" class="table-grid">
        <div class="dashboard-card">
          <div class="card-head">
            <div>
              <h4>Slow-Moving Stock</h4>
              <p>Purchased but not sold for 30+ days.</p>
            </div>
          </div>
          <div class="table-responsive">
            <a-table :columns="slowMovingColumns" :data-source="slowMovingRows" :pagination="false" row-key="itemid" size="small" />
          </div>
        </div>

        <div class="dashboard-card recent-panel">
          <div class="card-head panel-head">
            <div class="title-wrap">
              <span class="head-icon"><vue-feather type="flag"></vue-feather></span>
              <div>
                <h4>Recent Transactions</h4>
                <p>Latest sale activity with quick filters.</p>
              </div>
            </div>
            <button class="link-btn plain" @click="$router.push('/saleslist')">View All</button>
          </div>
          <div class="transaction-tabs">
            <button
              v-for="tab in transactionTabs"
              :key="tab.key"
              :class="{ active: activeTransactionTab === tab.key }"
              @click="activeTransactionTab = tab.key"
            >
              {{ tab.label }}
            </button>
          </div>
          <div class="recent-table">
            <div class="recent-head">
              <span>Date</span>
              <span>Customer</span>
              <span>Status</span>
              <span>Total</span>
            </div>
            <div v-for="row in activeTransactionRows" :key="row.key" class="recent-row">
              <span>{{ row.date }}</span>
              <div class="customer-cell">
                <span class="avatar">{{ row.initials }}</span>
                <div>
                  <strong>{{ row.customer }}</strong>
                  <small>{{ row.reference }}</small>
                </div>
              </div>
              <span class="status-pill" :class="row.statusClass">{{ row.status }}</span>
              <strong class="amount-cell">{{ row.total }}</strong>
            </div>
            <div v-if="!activeTransactionRows.length" class="empty-state compact">No transactions in this tab.</div>
          </div>
        </div>
      </div>
      <div v-else class="section-placeholder">
        <strong>Operational tables will load when this block reaches the viewport.</strong>
      </div>
    </section>

    <section ref="inventorySection" class="lazy-section" data-section="inventory">
      <div v-if="isSectionVisible('inventory')" class="table-grid bottom-grid">
        <div class="dashboard-card">
          <div class="card-head">
            <div>
              <h4>Stock Alerts</h4>
              <p>Low stock and out of stock items with thresholds.</p>
            </div>
            <button class="link-btn" @click="$router.push('/productlist')">Open Inventory</button>
          </div>
          <div class="alert-summary">
            <span class="pill pill-warn">Low: {{ stockAlerts.low_stock_count }}</span>
            <span class="pill pill-danger">Out: {{ stockAlerts.out_of_stock_count }}</span>
          </div>
          <div class="table-responsive">
            <a-table :columns="stockAlertColumns" :data-source="stockAlertRows" :pagination="false" row-key="rowkey" size="small" />
          </div>
        </div>

        <div class="dashboard-card">
          <div class="card-head">
            <div>
              <h4>Top Vendors</h4>
              <p>Highest purchase spend for the current month.</p>
            </div>
            <button class="link-btn" @click="$router.push('/supplierlist')">Open Suppliers</button>
          </div>
          <div class="mini-list" v-if="topVendors.length">
            <div class="mini-row" v-for="vendor in topVendors" :key="vendor.vendorid || vendor.vendorname">
              <div>
                <strong>{{ vendor.vendorname }}</strong>
                <span>{{ vendor.bill_count }} bills</span>
              </div>
              <div class="text-end">
                <strong>{{ formatCurrency(vendor.total_spend) }}</strong>
              </div>
            </div>
          </div>
          <div class="empty-state" v-else>No vendor spend data yet.</div>
        </div>
      </div>
      <div v-else class="section-placeholder">
        <strong>Inventory alerts and vendor insights will load lower down the page.</strong>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const slowMovingColumns = [
  { title: "Item", dataIndex: "itemname", key: "itemname" },
  { title: "Category", dataIndex: "categoryname", key: "categoryname" },
  { title: "Remaining Qty", dataIndex: "remainingqty", key: "remainingqty", align: "right" },
  { title: "Last Purchase", dataIndex: "latest_purchase_date", key: "latest_purchase_date" },
];

const recentBillsColumns = [
  { title: "Bill No", dataIndex: "billno", key: "billno" },
  { title: "Date", dataIndex: "billdate", key: "billdate" },
  { title: "Customer", dataIndex: "customername", key: "customername" },
  { title: "Payment", dataIndex: "paymentmode", key: "paymentmode" },
  { title: "Net Amount", dataIndex: "netamount", key: "netamount", align: "right" },
];

const stockAlertColumns = [
  { title: "Item", dataIndex: "itemname", key: "itemname" },
  { title: "Category", dataIndex: "categoryname", key: "categoryname" },
  { title: "Available", dataIndex: "available", key: "available", align: "right" },
  { title: "Min Stock", dataIndex: "minstock", key: "minstock", align: "right" },
];

export default {
  name: "indexpage",
  data() {
    return {
      loading: false,
      observer: null,
      fallbackScrollHandler: null,
      sectionState: {
        trend: false,
        analytics: false,
        operations: false,
        inventory: false,
      },
      sectionLoading: {
        trend: false,
        analytics: false,
        operations: false,
        inventory: false,
      },
      kpi: {
        revenue_today: 0,
        revenue_yesterday: 0,
        revenue_change_percent: 0,
        bills_today: 0,
        avg_bill_today: 0,
        cost_today: 0,
        profit_today: 0,
        margin_today_percent: 0,
        purchase_today: 0,
        purchase_yesterday: 0,
        purchase_change_percent: 0,
        purchases_month: 0,
        stock_value: 0,
        low_stock_count: 0,
        out_of_stock_count: 0,
        net_receivable: 0,
        receivable_parties: 0,
        net_payable: 0,
        payable_parties: 0,
      },
      profitSummary: {
        revenue_month: 0,
        cost_month: 0,
        gross_profit_month: 0,
        gross_margin_percent: 0,
      },
      salesVsPurchases: {
        months: [],
        sales: [],
        purchases: [],
      },
      profitTrend: {
        months: [],
        revenue: [],
        cost: [],
        gross_profit: [],
      },
      topItemsRevenue: [],
      topItemsProfit: [],
      categoryRevenue: [],
      slowMoving: [],
      stockAlerts: {
        low_stock_count: 0,
        out_of_stock_count: 0,
        low_stock_items: [],
        out_of_stock_items: [],
      },
      recentBills: [],
      topVendors: [],
      latestBankBalance: null,
      cheques: [],
      chequeForecast: [],
      activeTransactionTab: "sale",
      transactionTabs: [
        { key: "sale", label: "Sale" },
        { key: "purchase", label: "Purchase" },
        { key: "quotation", label: "Quotation" },
        { key: "expenses", label: "Expenses" },
        { key: "invoices", label: "Invoices" },
      ],
      slowMovingColumns,
      recentBillsColumns,
      stockAlertColumns,
    };
  },
  computed: {
    hasData() {
      return this.salesVsPurchases.months.length || this.recentBills.length || this.topItemsRevenue.length;
    },
    kpiCards() {
      return [
        {
          title: "Today's Sales",
          value: this.formatCurrency(this.kpi.revenue_today),
          note: this.changeNote(this.kpi.revenue_change_percent, "vs yesterday"),
          noteTone: this.changeTone(this.kpi.revenue_change_percent),
          icon: "trending-up",
          tone: "tone-orange",
        },
        {
          title: "Today's Profit",
          value: this.formatCurrency(this.kpi.profit_today),
          note: `${Number(this.kpi.profit_today || 0) < 0 ? "\u2193 Loss" : "\u2191 Profit"} \u00b7 Margin ${this.formatPercent(this.kpi.margin_today_percent)}`,
          noteTone: Number(this.kpi.profit_today || 0) < 0 ? "metric-note-danger" : "metric-note-up",
          icon: "pie-chart",
          tone: "tone-navy",
        },
        {
          title: "Stock Alerts",
          value: `${Number(this.kpi.low_stock_count || 0)} low stock`,
          note: `${Number(this.kpi.low_stock_count || 0)} low stock \u00b7 ${Number(this.kpi.out_of_stock_count || 0)} out of stock`,
          noteTone: Number(this.kpi.out_of_stock_count || 0) > 0 ? "metric-note-danger" : "metric-note-neutral",
          icon: "alert-circle",
          tone: "tone-teal",
        },
        {
          title: "Today's Purchase",
          value: this.formatCurrency(this.kpi.purchase_today),
          note: this.changeNote(this.kpi.purchase_change_percent, "vs yesterday"),
          noteTone: this.changeTone(this.kpi.purchase_change_percent),
          icon: "shopping-bag",
          tone: "tone-blue",
        },
      ];
    },
    cashCards() {
      return [
        {
          title: "Net cash position",
          value: this.latestBankBalance ? this.formatCurrency(this.latestBankBalance.closing_balance) : this.formatCurrency(0),
          note: this.latestBankBalance ? "Latest bank balance" : "No balance entry",
          tone: "note-up",
        },
        {
          title: "Receivable overdue 30d+",
          value: this.formatCurrency(this.kpi.net_receivable),
          note: `${Number(this.kpi.receivable_parties || 0)} customers`,
          tone: "note-danger",
        },
        {
          title: "Payable due 7 days",
          value: this.formatCurrency(this.kpi.net_payable),
          note: `${Number(this.kpi.payable_parties || 0)} vendors`,
          tone: "note-neutral",
        },
        {
          title: "Cheques pending clearance",
          value: this.formatCurrency(this.chequePendingAmount),
          note: `${Number(this.pendingChequesCount || 0)} cheques \u00b7 ${Number(this.bouncedThisWeekCount || 0)} bounced this week`,
          tone: "note-danger",
        },
      ];
    },
    chequeRows() {
      return this.cheques.slice(0, 4);
    },
    pendingChequesCount() {
      return this.cheques.filter((row) => row.status === "PENDING").length;
    },
    chequePendingAmount() {
      return this.cheques
        .filter((row) => row.status === "PENDING")
        .reduce((sum, row) => sum + Number(row.amount || 0), 0);
    },
    bouncedThisWeekCount() {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return this.cheques.filter((row) => {
        if (row.status !== "BOUNCED") return false;
        const created = new Date(row.created_at || row.cheque_date);
        return !Number.isNaN(created.getTime()) && created >= weekAgo;
      }).length;
    },
    monthSalesTotal() {
      return this.salesVsPurchases.sales.reduce((sum, value) => sum + Number(value || 0), 0);
    },
    monthPurchaseTotal() {
      return this.salesVsPurchases.purchases.reduce((sum, value) => sum + Number(value || 0), 0);
    },
    thisMonthRevenue() {
      return this.profitSummary.revenue_month || this.monthSalesTotal;
    },
    thisMonthExpense() {
      return this.profitSummary.cost_month || this.kpi.purchases_month;
    },
    salesPurchaseSeries() {
      return [
        { name: "Purchases", data: this.salesVsPurchases.purchases },
        { name: "Sales", data: this.salesVsPurchases.sales },
      ];
    },
    salesPurchaseChartOptions() {
      return {
        chart: { toolbar: { show: false }, stacked: true },
        colors: ["#fde3c5", "#f6a044"],
        dataLabels: { enabled: false },
        stroke: { width: 0 },
        xaxis: {
          categories: this.salesVsPurchases.months,
          labels: { rotate: 0, style: { colors: "#586176", fontSize: "12px" } },
        },
        yaxis: {
          labels: { formatter: (value) => this.compactCurrency(value) },
        },
        plotOptions: { bar: { borderRadius: 10, columnWidth: "52%" } },
        grid: { borderColor: "#e7ebf0", strokeDashArray: 5 },
        legend: { show: false },
        tooltip: { y: { formatter: (value) => this.formatCurrency(value) } },
      };
    },
    profitTrendSeries() {
      return [
        { name: "Revenue", data: this.profitTrend.revenue || [] },
        { name: "Expense", data: (this.profitTrend.cost || []).map((value) => Math.abs(Number(value || 0))) },
      ];
    },
    profitTrendChartOptions() {
      return {
        chart: { toolbar: { show: false }, parentHeightOffset: 0 },
        colors: ["#16a085", "#ef6c3e"],
        dataLabels: { enabled: false },
        stroke: { width: 0 },
        xaxis: {
          categories: this.profitTrend.months,
          labels: {
            rotate: -20,
            trim: true,
            hideOverlappingLabels: true,
            style: { colors: "#64748b", fontSize: "11px", fontWeight: 600 },
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => this.compactCurrency(value),
            style: { colors: "#64748b", fontSize: "11px" },
          },
        },
        plotOptions: { bar: { borderRadius: 7, borderRadiusApplication: "end", columnWidth: "44%" } },
        grid: { borderColor: "#e8edf3", strokeDashArray: 4, padding: { left: 4, right: 10, bottom: 8 } },
        legend: { show: true, position: "top", horizontalAlign: "right", fontWeight: 700, markers: { radius: 12 } },
        tooltip: { y: { formatter: (value) => this.formatCurrency(value) } },
      };
    },
    topRevenueChartSeries() {
      return [{ name: "Revenue", data: this.topItemsRevenue.map((row) => Number(row.revenue || 0)) }];
    },
    topRevenueChartOptions() {
      return {
        chart: { toolbar: { show: false }, parentHeightOffset: 0 },
        colors: ["#3b82f6", "#06b6d4", "#8b5cf6", "#f59e0b", "#10b981"],
        plotOptions: {
          bar: { horizontal: true, distributed: true, borderRadius: 7, borderRadiusApplication: "end", barHeight: "56%" },
        },
        dataLabels: {
          enabled: true,
          formatter: (value) => this.compactCurrency(value),
          style: { colors: ["#fff"], fontSize: "11px", fontWeight: 800 },
        },
        xaxis: {
          categories: this.topItemsRevenue.map((row) => this.shortLabel(row.itemname)),
          labels: {
            formatter: (value) => this.compactCurrency(value),
            style: { colors: "#64748b", fontSize: "11px" },
          },
        },
        yaxis: { labels: { style: { colors: "#475569", fontSize: "11px", fontWeight: 700 } } },
        grid: { borderColor: "#e8edf3", strokeDashArray: 4, padding: { left: 8, right: 16 } },
        legend: { show: false },
        tooltip: { y: { formatter: (value) => this.formatCurrency(value) } },
      };
    },
    topProfitChartSeries() {
      return [{ name: "Profit", data: this.topItemsProfit.map((row) => Number(row.profit || 0)) }];
    },
    topProfitChartOptions() {
      return {
        chart: { toolbar: { show: false }, parentHeightOffset: 0 },
        colors: ["#10b981", "#14b8a6", "#84cc16", "#f97316", "#6366f1"],
        plotOptions: {
          bar: { horizontal: true, distributed: true, borderRadius: 7, borderRadiusApplication: "end", barHeight: "56%" },
        },
        dataLabels: {
          enabled: true,
          formatter: (value) => this.compactCurrency(value),
          style: { colors: ["#fff"], fontSize: "11px", fontWeight: 800 },
        },
        xaxis: {
          categories: this.topItemsProfit.map((row) => this.shortLabel(row.itemname)),
          labels: {
            formatter: (value) => this.compactCurrency(value),
            style: { colors: "#64748b", fontSize: "11px" },
          },
        },
        yaxis: { labels: { style: { colors: "#475569", fontSize: "11px", fontWeight: 700 } } },
        grid: { borderColor: "#e8edf3", strokeDashArray: 4, padding: { left: 8, right: 16 } },
        legend: { show: false },
        tooltip: { y: { formatter: (value) => this.formatCurrency(value) } },
      };
    },
    categoryRevenueSeries() {
      return this.categoryRevenue.map((row) => Number(row.revenue || 0));
    },
    categoryRevenueOptions() {
      return {
        labels: this.categoryRevenue.map((row) => row.categoryname),
        chart: { toolbar: { show: false }, parentHeightOffset: 0 },
        dataLabels: {
          enabled: true,
          formatter: (value) => `${Number(value || 0).toFixed(0)}%`,
          style: { fontSize: "11px", fontWeight: 800 },
          dropShadow: { enabled: false },
        },
        legend: { position: "bottom", fontWeight: 700, itemMargin: { horizontal: 8, vertical: 5 } },
        stroke: { colors: ["#fff"], width: 4 },
        plotOptions: {
          pie: {
            donut: {
              size: "64%",
              labels: {
                show: true,
                name: { show: true, fontSize: "12px", color: "#64748b" },
                value: { show: true, fontSize: "20px", fontWeight: 800, formatter: (value) => this.compactCurrency(value) },
                total: {
                  show: true,
                  label: "Total",
                  fontSize: "12px",
                  color: "#64748b",
                  formatter: () => this.compactCurrency(this.categoryRevenueSeries.reduce((sum, value) => sum + Number(value || 0), 0)),
                },
              },
            },
          },
        },
        colors: ["#2563eb", "#f97316", "#10b981", "#e11d48", "#8b5cf6", "#f59e0b", "#06b6d4"],
        tooltip: { y: { formatter: (value) => this.formatCurrency(value) } },
      };
    },
    slowMovingRows() {
      return this.slowMoving.map((row) => ({
        ...row,
        latest_purchase_date: this.formatDate(row.latest_purchase_date),
      }));
    },
    recentBillsRows() {
      return this.recentBills.map((row) => ({
        ...row,
        billdate: this.formatDate(row.billdate),
        netamount: this.formatCurrency(row.netamount),
        paymentmode: row.paymentmode || "-",
        customername: row.customername || "Walk-in Customer",
      }));
    },
    activeTransactionRows() {
      if (this.activeTransactionTab === "sale") {
        return this.recentBillsRows.slice(0, 5).map((row, index) => ({
          key: row.saleid || `sale-${index}`,
          date: row.billdate,
          customer: row.customername,
          initials: this.getInitials(row.customername),
          reference: row.billno ? `#${row.billno}` : "#SALE",
          status: row.paymentmode === "-" ? "Completed" : row.paymentmode,
          statusClass: "status-completed",
          total: row.netamount,
        }));
      }
      return (this.placeholderTransactions[this.activeTransactionTab] || []).slice(0, 5);
    },
    placeholderTransactions() {
      return {
        purchase: [],
        quotation: [],
        expenses: [],
        invoices: [],
      };
    },
    stockAlertRows() {
      const lowRows = (this.stockAlerts.low_stock_items || []).map((row) => ({
        ...row,
        rowkey: `low-${row.itemid}-${row.locationid || "all"}`,
      }));
      const outRows = (this.stockAlerts.out_of_stock_items || []).map((row) => ({
        ...row,
        rowkey: `out-${row.itemid}-${row.locationid || "all"}`,
      }));
      return [...outRows, ...lowRows];
    },
  },
  methods: {
    isSectionVisible(section) {
      return this.sectionState[section] || this.sectionLoading[section];
    },
    async loadOverview() {
      this.loading = true;
      try {
        const [kpiResponse, profitResponse, balanceResponse, chequesResponse, forecastResponse] = await Promise.allSettled([
          axios.get("/v1/dashboard/kpi-summary"),
          axios.get("/v1/dashboard/profit-summary"),
          axios.get("/v1/bank/daily-balance/latest"),
          axios.get("/v1/cheques"),
          axios.get("/v1/cheques/forecast"),
        ]);
        if (kpiResponse.status === "fulfilled") {
          this.kpi = kpiResponse.value.data || this.kpi;
        }
        if (profitResponse.status === "fulfilled") {
          this.profitSummary = profitResponse.value.data || this.profitSummary;
        }
        if (balanceResponse.status === "fulfilled") {
          this.latestBankBalance = balanceResponse.value.data || null;
        }
        if (chequesResponse.status === "fulfilled") {
          this.cheques = chequesResponse.value.data || [];
        }
        if (forecastResponse.status === "fulfilled") {
          this.chequeForecast = forecastResponse.value.data || [];
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to load dashboard overview",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.loading = false;
      }
    },
    async loadSection(section, force = false) {
      if (this.sectionLoading[section]) {
        return;
      }
      if (!force && this.sectionState[section]) {
        return;
      }

      this.sectionLoading[section] = true;
      try {
        if (section === "trend") {
          const [salesPurchaseResponse, profitTrendResponse] = await Promise.all([
            axios.get("/v1/dashboard/sales-vs-purchases"),
            axios.get("/v1/dashboard/gross-profit-trend"),
          ]);
          this.salesVsPurchases = salesPurchaseResponse.data || this.salesVsPurchases;
          this.profitTrend = profitTrendResponse.data || this.profitTrend;
        } else if (section === "analytics") {
          const [topRevenueResponse, topProfitResponse, categoryResponse] = await Promise.all([
            axios.get("/v1/dashboard/top-items-revenue"),
            axios.get("/v1/dashboard/top-items-profit"),
            axios.get("/v1/dashboard/category-revenue"),
          ]);
          this.topItemsRevenue = topRevenueResponse.data || [];
          this.topItemsProfit = topProfitResponse.data || [];
          this.categoryRevenue = categoryResponse.data || [];
        } else if (section === "operations") {
          const [slowMovingResponse, recentBillsResponse] = await Promise.all([
            axios.get("/v1/dashboard/slow-moving"),
            axios.get("/v1/dashboard/recent-bills", { params: { limit: 5 } }),
          ]);
          this.slowMoving = slowMovingResponse.data || [];
          this.recentBills = (recentBillsResponse.data || []).slice(0, 5);
        } else if (section === "inventory") {
          const [stockAlertsResponse, topVendorsResponse] = await Promise.all([
            axios.get("/v1/dashboard/stock-alerts"),
            axios.get("/v1/dashboard/top-vendors"),
          ]);
          this.stockAlerts = stockAlertsResponse.data || this.stockAlerts;
          this.topVendors = topVendorsResponse.data || [];
        }

        this.sectionState[section] = true;
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: `Failed to load ${section} section`,
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.sectionLoading[section] = false;
      }
    },
    observeSections() {
      const sections = [
        { ref: "trendSection", name: "trend" },
        { ref: "analyticsSection", name: "analytics" },
        { ref: "operationsSection", name: "operations" },
        { ref: "inventorySection", name: "inventory" },
      ];

      const fallbackLoader = () => {
        sections.forEach(({ ref, name }) => {
          const element = this.$refs[ref];
          if (!element || this.sectionState[name]) {
            return;
          }
          const rect = element.getBoundingClientRect();
          if (rect.top <= window.innerHeight + 180) {
            this.loadSection(name);
          }
        });
      };

      if (!("IntersectionObserver" in window)) {
        fallbackLoader();
        window.addEventListener("scroll", fallbackLoader, { passive: true });
        this.fallbackScrollHandler = fallbackLoader;
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }
            const section = entry.target.dataset.section;
            if (section) {
              this.loadSection(section);
            }
            this.observer?.unobserve(entry.target);
          });
        },
        {
          root: null,
          rootMargin: "180px 0px",
          threshold: 0.05,
        }
      );

      sections.forEach(({ ref }) => {
        const element = this.$refs[ref];
        if (element) {
          this.observer.observe(element);
        }
      });
    },
    async refreshDashboard() {
      await this.loadOverview();
      const loadedSections = Object.keys(this.sectionState).filter((section) => this.sectionState[section]);
      await Promise.all(loadedSections.map((section) => this.loadSection(section, true)));
    },
    formatCurrency(value) {
      return `\u20b9${Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;
    },
    compactCurrency(value) {
      const num = Number(value || 0);
      if (Math.abs(num) >= 10000000) return `\u20b9${(num / 10000000).toFixed(1)}Cr`;
      if (Math.abs(num) >= 100000) return `\u20b9${(num / 100000).toFixed(1)}L`;
      if (Math.abs(num) >= 1000) return `\u20b9${(num / 1000).toFixed(1)}K`;
      return `\u20b9${num.toFixed(0)}`;
    },
    shortLabel(value, maxLength = 24) {
      const label = String(value || "Unknown item").trim();
      if (label.length <= maxLength) return label;
      return `${label.slice(0, maxLength - 1)}...`;
    },
    formatPercent(value) {
      return `${Number(value || 0).toFixed(1)}%`;
    },
    changeTone(value) {
      const numericValue = Number(value || 0);
      if (numericValue > 0) return "metric-note-up";
      if (numericValue < 0) return "metric-note-danger";
      return "metric-note-neutral";
    },
    changeNote(value, label) {
      const numericValue = Number(value || 0);
      const arrow = numericValue > 0 ? "\u2191" : numericValue < 0 ? "\u2193" : "\u2192";
      return `${arrow} ${this.formatPercent(Math.abs(numericValue))} ${label}`;
    },
    getInitials(value) {
      return String(value || "WC")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("") || "WC";
    },
    prettyStatus(status) {
      return String(status || "Pending")
        .toLowerCase()
        .replace(/(^|_|\s)\w/g, (match) => match.toUpperCase().replace("_", " "));
    },
    chequeStatusClass(status) {
      const value = String(status || "").toLowerCase();
      if (value === "cleared") return "status-completed";
      if (value === "bounced" || value === "cancelled") return "status-draft";
      return "status-pending";
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },
  async mounted() {
    await this.loadOverview();
    this.observeSections();
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.fallbackScrollHandler) {
      window.removeEventListener("scroll", this.fallbackScrollHandler);
      this.fallbackScrollHandler = null;
    }
  },
};
</script>

<style scoped>
.dashboard-shell {
  display: flex;
  flex-direction: column;
  gap: 18px;
  color: #0b1324;
}
.pos-dashboard {
  --dash-orange: #f6a044;
  --dash-orange-soft: #fff2e3;
  --dash-border: #e5e8ee;
  --dash-muted: #687083;
  --dash-heading: #0b1324;
}
.lazy-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.dashboard-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.dashboard-head h2 {
  margin: 0;
  font-size: 24px;
  color: #0f172a;
  font-weight: 800;
}
.dashboard-head p {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 13px;
}
.refresh-btn,
.link-btn {
  border: none;
  border-radius: 8px;
  background: var(--dash-orange);
  color: #fff;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.refresh-btn:hover,
.link-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(246, 160, 68, 0.2);
}
.refresh-btn:disabled {
  opacity: 0.7;
}
.link-btn {
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
  padding: 8px 12px;
}
.link-btn.plain {
  background: transparent;
  border: none;
  color: #111827;
  text-decoration: underline;
  box-shadow: none;
}
.loading-banner,
.empty-state {
  padding: 14px 16px;
  border-radius: 14px;
  background: #f8fafc;
  color: #64748b;
  border: 1px dashed #cbd5e1;
}
.section-placeholder {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(255, 247, 237, 0.9), rgba(248, 250, 252, 0.95));
  color: #64748b;
  border: 1px dashed #cbd5e1;
  text-align: center;
}
.section-placeholder strong {
  color: #334155;
  font-size: 14px;
  font-weight: 700;
}
.kpi-grid,
.cash-grid,
.chart-grid,
.analytics-grid,
.table-grid {
  display: grid;
  gap: 16px;
}
.kpi-grid {
  grid-template-columns: repeat(4, minmax(180px, 1fr));
}
.cash-grid {
  grid-template-columns: minmax(0, 1.45fr) minmax(340px, 0.55fr);
}
.chart-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.analytics-grid {
  grid-template-columns: 1fr 1fr 1fr;
}
.table-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.section-label {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid #d6d3d1;
  color: #6b7280;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.metric-card,
.dashboard-card {
  background: #fff;
  border: 1px solid var(--dash-border);
  border-radius: 8px;
  box-shadow: 0 5px 18px rgba(15, 23, 42, 0.04);
}
.metric-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 26px;
  min-height: 112px;
  position: relative;
  overflow: hidden;
  color: #fff;
  border: 0;
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.12);
}
.metric-card::after {
  content: "";
  position: absolute;
  inset: -48px -34px auto auto;
  width: 136px;
  height: 136px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.13);
}
.metric-card::before {
  content: "";
  position: absolute;
  inset: auto auto -52px 42%;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
}
.metric-icon {
  width: 56px;
  height: 56px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 800;
  flex: 0 0 56px;
  background: #fff;
  color: currentColor;
  position: relative;
  z-index: 1;
}
.metric-feather,
.head-icon :deep(svg),
.btn-feather {
  width: 18px;
  height: 18px;
  stroke-width: 2.4;
}
.metric-feather {
  width: 20px;
  height: 20px;
}
.btn-feather {
  margin-right: 4px;
  vertical-align: -4px;
}
.metric-content {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
  position: relative;
  z-index: 1;
}
.metric-label {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.92);
  font-weight: 700;
}
.metric-value {
  font-size: 24px;
  line-height: 1.15;
  color: #fff;
  font-weight: 800;
  word-break: break-word;
}
.metric-note {
  align-self: flex-start;
  border-radius: 7px;
  padding: 5px 9px;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.94);
}
.tone-orange { background: linear-gradient(135deg, #ffa73d 0%, #ff922b 100%); }
.tone-navy { background: linear-gradient(135deg, #173253 0%, #0d2340 100%); }
.tone-teal { background: linear-gradient(135deg, #36a892 0%, #238b79 100%); }
.tone-blue { background: linear-gradient(135deg, #4968f2 0%, #3155e7 100%); }
.tone-green { background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%); }
.tone-gold { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
.tone-red { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
.tone-purple { background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%); }
.tone-orange .metric-icon { color: #f97316; }
.tone-navy .metric-icon { color: #123153; }
.tone-teal .metric-icon { color: #0f8f7d; }
.tone-blue .metric-icon { color: #3155e7; }
.tone-green .metric-icon { color: #16a34a; }
.tone-gold .metric-icon { color: #d97706; }
.tone-red .metric-icon { color: #dc2626; }
.tone-purple .metric-icon { color: #6d28d9; }
.metric-note-up { color: #16a34a; }
.metric-note-danger { color: #dc2626; }
.metric-note-neutral { color: #334155; }
.dashboard-card {
  padding: 16px;
  min-width: 0;
}
.card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}
.panel-head {
  padding-bottom: 14px;
  border-bottom: 1px solid #eef0f4;
}
.title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.head-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--dash-orange-soft);
  color: #f36f21;
  font-size: 19px;
  flex: 0 0 40px;
}
.head-icon.warn {
  color: #d97706;
}
.head-icon.danger {
  background: #fff0ed;
  color: #ff3b30;
}
.card-head h4 {
  margin: 0;
  color: #0f172a;
  font-size: 20px;
  font-weight: 800;
}
.card-head p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
}
.cash-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.cash-metric {
  border: 1px solid #eef0f4;
  border-radius: 8px;
  padding: 14px;
  min-height: 112px;
  background: #fff;
}
.cash-metric span,
.cash-metric small {
  display: block;
}
.cash-metric span {
  color: #667085;
  font-size: 13px;
  font-weight: 700;
}
.cash-metric strong {
  display: block;
  margin-top: 8px;
  color: #111827;
  font-size: 21px;
  line-height: 1.15;
}
.cash-metric small {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 800;
}
.note-up {
  color: #1f9d75;
}
.note-danger {
  color: #e0432f;
}
.note-neutral {
  color: #667085;
}
.cheque-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cheque-row,
.recent-row {
  display: grid;
  align-items: center;
  gap: 12px;
}
.cheque-row {
  grid-template-columns: minmax(0, 1fr) auto;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f6;
}
.cheque-row:last-child {
  border-bottom: none;
}
.cheque-row strong,
.cheque-row span {
  display: block;
}
.cheque-row span {
  color: #667085;
  font-size: 12px;
}
.range-tabs {
  display: inline-flex;
  border: 1px solid var(--dash-border);
  border-radius: 7px;
  overflow: hidden;
}
.range-tabs button,
.year-btn,
.transaction-tabs button {
  border: 0;
  background: #fff;
  color: #0f172a;
  font-weight: 700;
}
.range-tabs button {
  min-width: 54px;
  padding: 10px 12px;
  border-right: 1px solid var(--dash-border);
}
.range-tabs button:last-child {
  border-right: 0;
}
.range-tabs .active {
  background: var(--dash-orange);
  color: #fff;
}
.year-btn {
  border: 1px solid var(--dash-border);
  border-radius: 7px;
  padding: 8px 12px;
}
.chart-summary,
.stat-summary {
  display: flex;
  gap: 10px;
  margin: 12px 0 4px;
  flex-wrap: wrap;
}
.chart-summary span,
.stat-summary div {
  border: 1px solid var(--dash-border);
  border-radius: 8px;
  padding: 12px;
  color: #667085;
  font-weight: 700;
}
.chart-summary span {
  min-width: 124px;
}
.chart-summary strong,
.stat-summary span {
  display: block;
}
.chart-summary i {
  width: 8px;
  height: 8px;
  display: inline-block;
  border-radius: 50%;
  background: #fde3c5;
  margin-right: 6px;
}
.chart-summary i.sales-dot {
  background: var(--dash-orange);
}
.stat-summary strong {
  display: inline-block;
  margin-bottom: 4px;
  font-size: 21px;
  line-height: 1.2;
}
.success-text {
  color: #2f9488;
}
.danger-text {
  color: #e04a25;
}
.transaction-tabs {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  border-bottom: 1px solid #eef0f4;
  margin: -2px -16px 0;
}
.transaction-tabs button {
  min-height: 48px;
  border-bottom: 2px solid transparent;
}
.transaction-tabs .active {
  color: #f36f21;
  border-color: var(--dash-orange);
}
.recent-table {
  overflow-x: auto;
}
.recent-head,
.recent-row {
  grid-template-columns: minmax(110px, 0.9fr) minmax(190px, 1.6fr) minmax(120px, 0.8fr) minmax(100px, 0.7fr);
  min-width: 640px;
}
.recent-head {
  display: grid;
  gap: 12px;
  padding: 12px 0;
  color: #0b1324;
  font-weight: 800;
}
.recent-row {
  min-height: 70px;
  border-top: 1px solid #f1f3f6;
}
.customer-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}
.avatar {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e9edf3;
  color: #111827;
  font-size: 12px;
  font-weight: 800;
  flex: 0 0 40px;
}
.customer-cell strong,
.customer-cell small {
  display: block;
}
.customer-cell small {
  color: #f36f21;
  font-size: 13px;
}
.amount-cell {
  font-size: 18px;
  color: #0b1324;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  border-radius: 6px;
  padding: 5px 10px;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}
.status-completed {
  background: #4dbb86;
}
.status-draft {
  background: #dc2d92;
}
.status-pending {
  background: #f6a044;
}
.compact {
  padding: 12px;
  margin-top: 10px;
}
.mini-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mini-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f8fafc;
}
.mini-row strong {
  display: block;
  color: #0f172a;
}
.mini-row span {
  color: #64748b;
  font-size: 12px;
}
.text-end {
  text-align: right;
}
.alert-summary {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
}
.pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}
.pill-warn {
  background: #fff7ed;
  color: #c2410c;
}
.pill-danger {
  background: #fef2f2;
  color: #dc2626;
}
:deep(.ant-table) {
  font-size: 13px;
}
:deep(.ant-table-thead > tr > th) {
  background: #f8fafc;
  color: #475569;
  font-weight: 700;
}
.chart-card :deep(.apexcharts-canvas) {
  margin-top: 6px;
  max-width: 100%;
}
.chart-card :deep(.apexcharts-legend-text) {
  color: #475569 !important;
}
.chart-card :deep(.apexcharts-bar-area) {
  filter: drop-shadow(0 5px 8px rgba(15, 23, 42, 0.12));
}
.analytics-grid .chart-card {
  overflow: hidden;
}
.analytics-grid .card-head {
  min-height: 64px;
}
.analytics-grid .card-head h4 {
  font-size: 18px;
  line-height: 1.25;
}
.analytics-grid .card-head p {
  line-height: 1.35;
}
.chart-card :deep(.apexcharts-gridline) {
  stroke: #e5e7eb;
}
.chart-card :deep(.apexcharts-xaxis-label),
.chart-card :deep(.apexcharts-yaxis-label) {
  fill: #6b7280;
}
@media (max-width: 1400px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .cash-grid {
    grid-template-columns: 1fr;
  }
  .cash-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .analytics-grid {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 992px) {
  .dashboard-head,
  .card-head {
    flex-direction: column;
    align-items: flex-start;
  }
  .kpi-grid,
  .cash-grid,
  .chart-grid,
  .analytics-grid,
  .table-grid {
    grid-template-columns: 1fr;
  }
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .cash-metrics {
    grid-template-columns: 1fr 1fr;
  }
  .range-tabs {
    width: 100%;
  }
  .range-tabs button {
    min-width: 0;
    flex: 1;
  }
  .refresh-btn,
  .link-btn {
    width: 100%;
    justify-content: center;
  }
}
@media (max-width: 767px) {
  .dashboard-shell {
    gap: 14px;
  }
  .dashboard-head h2 {
    font-size: 20px;
  }
  .dashboard-head p {
    font-size: 12px;
  }
  .metric-card,
  .dashboard-card,
  .section-placeholder {
    border-radius: 8px;
  }
  .metric-card {
    min-height: auto;
    padding: 14px;
  }
  .kpi-grid {
    grid-template-columns: 1fr;
  }
  .metric-icon {
    width: 48px;
    height: 48px;
    flex-basis: 48px;
  }
  .metric-value {
    font-size: 20px;
  }
  .metric-label {
    font-size: 14px;
  }
  .dashboard-card {
    padding: 14px;
  }
  .cash-metrics,
  .stat-summary,
  .chart-summary {
    grid-template-columns: 1fr;
    flex-direction: column;
  }
  .stat-summary div,
  .chart-summary span {
    width: 100%;
  }
  .transaction-tabs {
    grid-template-columns: repeat(5, minmax(92px, 1fr));
    overflow-x: auto;
    margin-left: -14px;
    margin-right: -14px;
  }
  .card-head h4 {
    font-size: 18px;
  }
  .section-label {
    font-size: 12px;
  }
  .chart-card :deep(.apexcharts-svg) {
    overflow: visible;
  }
  .chart-card :deep(.apexcharts-yaxis-label),
  .chart-card :deep(.apexcharts-xaxis-label) {
    font-size: 10px;
  }
  :deep(.ant-table) {
    font-size: 12px;
  }
}
@media (max-width: 575px) {
  .dashboard-shell {
    margin-left: -4px;
    margin-right: -4px;
  }
  .title-wrap {
    align-items: flex-start;
  }
  .head-icon {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
  }
  .analytics-grid .card-head {
    min-height: 0;
  }
  .recent-head,
  .recent-row {
    min-width: 560px;
    grid-template-columns: minmax(90px, 0.8fr) minmax(170px, 1.5fr) minmax(105px, 0.8fr) minmax(90px, 0.7fr);
  }
}
</style>
